/**
 * @tempest-limits file-lines — the file is one hand-written lookup table plus the
 * two functions that read it. Splitting a table by row count would put pairs that
 * belong together (`format_*`, `electrical_*`) in different files and make the
 * next hand-written pair a question of which file it lands in; the length is the
 * vocabulary's, not the code's.
 */
import type { IconName } from "./generated/icon-name";

/**
 * The slug `fromMaterialSymbol` falls back to when a code is unknown.
 *
 * A neutral glyph rather than nothing: a category created in an admin with a
 * code this table has not learned yet still has to draw something, or it opens a
 * hole in the grid and the bug reads as a layout problem instead of a missing
 * mapping.
 *
 * Lucide's `circle-help` is a deprecated alias of this slug. `<Icon>` resolves
 * aliases, so both render — but the bridge emits the canonical name, since a
 * value that gets persisted should not be one lucide has already renamed.
 */
export const MATERIAL_SYMBOL_FALLBACK: IconName = "circle-question-mark";

/**
 * Material Symbols code → lucide slug, for backends that store `icon_code`.
 *
 * Every Python backend we write stores a category icon as a Material Symbol
 * (`build`, `format_paint`, `electrical_services`) — the vocabulary Flutter,
 * Android and the administrative seeds already speak. The SDK speaks lucide in
 * kebab-case. The two lists do not meet, and the failure is nastier than an
 * empty screen: a handful of codes collide by accident, so roughly one row in
 * ten draws the right icon and the bug reads as "some icons went missing".
 *
 * **Still not the full vocabulary.** Material Symbols publishes ~6100 names and
 * almost none of them will ever appear in an `icon_code` of ours, so this grows
 * on demand, one hand-written pair at a time — a map generated by name
 * heuristics gets it badly wrong, starting with `build`, which is a wrench in
 * Material Symbols and nothing to do with construction.
 *
 * **How the codes were chosen.** Two lots, merged. The first was the trades seed
 * the bridge started as. The second is the head of Material Symbols' own
 * published popularity ranking (`fonts.google.com/metadata/icons`), which is
 * measured usage rather than a guess at which names a seed will contain — every
 * key was checked to exist in that vocabulary before landing.
 *
 * **Where the two lots disagreed**, six keys, the tie went to whichever mapping
 * keeps two distinct Material Symbols names distinct in lucide: `error` →
 * `circle-alert` because `cancel` already owns `circle-x`; `payments` →
 * `banknote` because `account_balance_wallet` already owns `wallet`;
 * `receipt_long` → `receipt-text` because `receipt` already owns `receipt`;
 * `construction` → itself because `engineering` already owns `hard-hat`; and
 * `today` → `calendar` with `event` → `calendar-days`, splitting the generic
 * calendar names from the dated one.
 *
 * Every target is checked mechanically against the slug list by
 * `material-symbols.test.ts`, so a pair pointing at a name lucide does not ship
 * — or at one it has since deprecated — fails the suite instead of reaching a
 * grid. What no test can check is whether the *chosen* icon is the right
 * metaphor, which is why pairs go in by hand.
 *
 * **Approximations, deliberate and not one-to-one** (the full list lives in
 * `docs/icons.md`):
 *
 * - `plumbing` → `shower-head`, because lucide has no pipe.
 * - `build`, `handyman` and `hardware` all land on `wrench` — Material Symbols
 *   distinguishes the tools, lucide does not.
 * - `pedal_bike`, `two_wheeler` and `delivery_dining` all land on `bike`.
 * - `dentistry` → `face-slightly-smiling`, since lucide ships no tooth.
 * - `iron` → `shirt` and `cleaning_services` → `spray-can`: the activity, not
 *   the implement.
 * - `roofing` → `house`, the same glyph as `home`, because lucide has no roof.
 *
 * The identity pairs are here on purpose. Their names happen to match in both
 * vocabularies, so they already render today — leaving them out would send them
 * to {@link MATERIAL_SYMBOL_FALLBACK} and make this bridge a regression for
 * exactly the codes that used to work.
 *
 * @see fromMaterialSymbol — the lookup you normally call.
 */
export const materialToLucide: Readonly<Record<string, IconName>> = {
    ac_unit: "snowflake",
    account_balance: "landmark",
    account_balance_wallet: "wallet",
    account_box: "square-user",
    account_circle: "circle-user",
    add: "plus",
    add_circle: "circle-plus",
    add_circle_outline: "circle-plus",
    add_shopping_cart: "shopping-cart",
    admin_panel_settings: "shield-user",
    analytics: "chart-column",
    apps: "layout-grid",
    arrow_back: "arrow-left",
    arrow_back_ios: "chevron-left",
    arrow_back_ios_new: "chevron-left",
    arrow_downward: "arrow-down",
    arrow_drop_down: "chevron-down",
    arrow_forward: "arrow-right",
    arrow_forward_ios: "chevron-right",
    arrow_right: "chevron-right",
    arrow_right_alt: "arrow-right",
    arrow_upward: "arrow-up",
    article: "newspaper",
    assignment: "clipboard-list",
    attach_file: "paperclip",
    attach_money: "dollar-sign",
    autorenew: "refresh-cw",
    badge: "id-card",
    balance: "scale",
    bar_chart: "chart-column",
    battery_full: "battery-full",
    bluetooth: "bluetooth",
    bolt: "zap",
    bookmark: "bookmark",
    brush: "brush",
    build: "wrench",
    business: "building-2",
    cake: "cake",
    calculate: "calculator",
    calendar_month: "calendar",
    calendar_today: "calendar",
    call: "phone",
    campaign: "megaphone",
    cancel: "circle-x",
    car_repair: "car",
    carpenter: "hammer",
    category: "shapes",
    chair: "armchair",
    chat: "message-circle",
    check: "check",
    check_box: "square-check",
    check_box_outline_blank: "square",
    check_circle: "circle-check",
    check_circle_outline: "circle-check",
    chevron_left: "chevron-left",
    chevron_right: "chevron-right",
    child_care: "baby",
    circle: "circle",
    cleaning_services: "spray-can",
    clear: "x",
    close: "x",
    cloud: "cloud",
    code: "code",
    computer: "monitor",
    construction: "construction",
    content_copy: "copy",
    content_cut: "scissors",
    credit_card: "credit-card",
    dark_mode: "moon",
    dashboard: "layout-dashboard",
    date_range: "calendar-range",
    delete: "trash",
    delete_forever: "trash",
    delete_outline: "trash",
    delivery_dining: "bike",
    dentistry: "face-slightly-smiling",
    description: "file-text",
    directions_car: "car",
    done: "check",
    done_all: "check-check",
    door_front: "door-open",
    download: "download",
    east: "arrow-right",
    edit: "pencil",
    edit_note: "pencil-line",
    electrical_services: "plug-zap",
    email: "mail",
    emoji_events: "trophy",
    engineering: "hard-hat",
    error: "circle-alert",
    error_outline: "circle-alert",
    event: "calendar-days",
    expand_less: "chevron-up",
    expand_more: "chevron-down",
    explore: "compass",
    face: "face-slightly-smiling",
    fact_check: "clipboard-check",
    favorite: "heart",
    favorite_border: "heart",
    file_download: "download",
    file_upload: "upload",
    filter_alt: "funnel",
    filter_list: "funnel",
    fingerprint: "fingerprint-pattern",
    fitness_center: "dumbbell",
    flight: "plane",
    folder: "folder",
    format_list_bulleted: "list",
    format_paint: "paint-roller",
    forum: "messages-square",
    gavel: "gavel",
    grade: "star",
    grass: "sprout",
    grid_view: "layout-grid",
    group: "users",
    groups: "users",
    handyman: "wrench",
    hardware: "wrench",
    help: "circle-question-mark",
    help_outline: "circle-question-mark",
    highlight_off: "circle-x",
    history: "rotate-ccw-clock",
    home: "house",
    hotel: "bed-double",
    image: "image",
    info: "info",
    insights: "chart-line",
    inventory: "package",
    inventory_2: "package",
    iron: "shirt",
    key: "key",
    kitchen: "refrigerator",
    language: "globe",
    laptop: "laptop",
    light_mode: "sun",
    lightbulb: "lightbulb",
    link: "link",
    list: "list",
    list_alt: "list",
    local_bar: "wine",
    local_cafe: "coffee",
    local_florist: "flower",
    local_gas_station: "fuel",
    local_laundry_service: "washing-machine",
    local_offer: "tag",
    local_shipping: "truck",
    local_taxi: "car-taxi-front",
    location_on: "map-pin",
    lock: "lock",
    lock_open: "lock-open",
    login: "log-in",
    logout: "log-out",
    mail: "mail",
    mail_outline: "mail",
    manage_accounts: "user-cog",
    map: "map",
    medical_services: "stethoscope",
    menu: "menu",
    menu_book: "book-open",
    mic: "mic",
    mode_edit: "pencil",
    monetization_on: "circle-dollar-sign",
    more_horiz: "ellipsis",
    more_vert: "ellipsis-vertical",
    music_note: "music",
    navigate_next: "chevron-right",
    note_add: "file-plus",
    notifications: "bell",
    notifications_active: "bell-ring",
    open_in_new: "external-link",
    paid: "circle-dollar-sign",
    palette: "palette",
    pause: "pause",
    payments: "banknote",
    pedal_bike: "bike",
    people: "users",
    perm_identity: "user",
    person: "user",
    person_add: "user-plus",
    person_outline: "user",
    pest_control: "bug",
    pets: "paw-print",
    phone: "phone",
    phone_iphone: "smartphone",
    photo_camera: "camera",
    picture_as_pdf: "file-text",
    pie_chart: "chart-pie",
    place: "map-pin",
    play_arrow: "play",
    play_circle: "circle-play",
    play_circle_filled: "circle-play",
    plumbing: "shower-head",
    power_settings_new: "power",
    print: "printer",
    public: "globe",
    qr_code_scanner: "scan-line",
    question_answer: "messages-square",
    radio_button_checked: "circle-dot",
    radio_button_unchecked: "circle",
    receipt: "receipt",
    receipt_long: "receipt-text",
    refresh: "refresh-cw",
    remove: "minus",
    remove_circle_outline: "circle-minus",
    reorder: "list",
    report_problem: "triangle-alert",
    restart_alt: "rotate-ccw",
    restaurant: "utensils",
    roofing: "house",
    room: "map-pin",
    router: "router",
    save: "save",
    savings: "piggy-bank",
    schedule: "clock",
    school: "graduation-cap",
    search: "search",
    security: "shield-check",
    send: "send",
    settings: "settings",
    share: "share-2",
    shield: "shield",
    shopping_bag: "shopping-bag",
    shopping_cart: "shopping-cart",
    smartphone: "smartphone",
    soap: "soap-dispenser-droplet",
    sort: "arrow-down-up",
    spa: "flower-2",
    star: "star",
    star_border: "star",
    star_rate: "star",
    store: "store",
    storefront: "store",
    support_agent: "headset",
    sync: "refresh-cw",
    task_alt: "circle-check",
    timer: "timer",
    today: "calendar",
    toggle_on: "toggle-right",
    translate: "languages",
    trending_up: "trending-up",
    tune: "sliders-horizontal",
    tv: "tv",
    two_wheeler: "bike",
    upload: "upload",
    upload_file: "upload",
    vaccines: "syringe",
    verified: "badge-check",
    verified_user: "shield-check",
    videocam: "video",
    view_list: "list",
    visibility: "eye",
    visibility_off: "eye-off",
    vpn_key: "key-round",
    warehouse: "warehouse",
    warning: "triangle-alert",
    warning_amber: "triangle-alert",
    watch_later: "clock",
    water_drop: "droplet",
    wifi: "wifi",
    work: "briefcase",
    yard: "trees",
};

/**
 * Translate a Material Symbols code into a lucide slug, always returning one.
 *
 * Never returns `undefined`: an unknown code resolves to `fallback`, so a row
 * whose `icon_code` this table has not learned yet still renders. The input is
 * trimmed and lower-cased before lookup, because a seed written by hand is the
 * kind of source that carries stray whitespace and the odd capital.
 *
 * Nothing else in `/icons` imports this module, so an app that does not store
 * Material Symbols never pays for the table.
 *
 * @example
 * <Icon name={fromMaterialSymbol(category.icon_code)} size={20} />
 *
 * @example
 * <Icon name={fromMaterialSymbol(category.icon_code, "folder")} size={20} />
 *
 * @param code - A Material Symbols name, or `null`/`undefined` for a row that has none.
 * @param fallback - Slug to use when `code` is empty or unknown. Defaults to {@link MATERIAL_SYMBOL_FALLBACK}.
 * @returns A lucide slug `<Icon>` can render.
 */
export function fromMaterialSymbol(
    code: string | null | undefined,
    fallback: IconName = MATERIAL_SYMBOL_FALLBACK,
): IconName {
    if (!code) return fallback;
    return materialToLucide[code.trim().toLowerCase()] ?? fallback;
}
